import React from "react";
import ContentEditable from "react-contenteditable";

/**
 * Helper function to create mention chip HTML with consistent styling
 * Note: For chat display, we create read-only chips without close buttons
 */
export const createMentionChipHtml = (
  typeName,
  title,
  entityType,
  entityId,
  subtitle
) => {
  const maxContentLength = 20;
  const isContentTruncated = title.length > maxContentLength;
  const truncatedTitle = isContentTruncated
    ? title.substring(0, maxContentLength - 3) + "..."
    : title;

  const displayContent = `@${typeName}:${truncatedTitle}`;

  // Get mention chip colors from CSS variables
  const getMentionChipColors = () => {
    // Use CSS variables with fallback
    if (typeof document !== "undefined") {
      const isDark = document.documentElement.classList.contains("dark");
      return {
        background: isDark ? "#4F6B80" : "#E3F2FD",
        text: isDark ? "#BFDBFE" : "#1976D2",
      };
    }
    // Final fallback
    return {
      background: "#E3F2FD",
      text: "#1976D2",
    };
  };

  const colors = getMentionChipColors();

  // Create visual chip for chat display (read-only, no close button)
  return `<span contenteditable="false" class="mention-chip" ${
    isContentTruncated ? `title="@${typeName}:${title}"` : ""
  } style="display:inline-flex;align-items:center;padding:1px 4px 1px 6px;border-radius:16px;background:${
    colors.background
  };color:${colors.text};font-size:14px;border:1px solid ${
    colors.background
  };margin:0 1px;gap:4px;">${displayContent}<entity type="${entityType}" name="${title}" id="${entityId}" subtitle="${
    subtitle || ""
  }" typeName="${typeName}" style="display:none;"></entity></span>`;
};

/**
 * Convert text with entity tags to HTML (for REQ messages in chat-block)
 */
export const convertTextWithXmlTagsToHtml = (text) => {
  if (!text) return "";

  let htmlContent = "";
  let lastIndex = 0;

  // Updated regex to match entity tags with attributes
  const entityRegex = /<entity\s+([^>]*?)(?:\s*\/)?>\s*(?:<\/entity>)?/g;
  let match;

  while ((match = entityRegex.exec(text)) !== null) {
    // Add any text before this entity tag
    if (match.index > lastIndex) {
      htmlContent += text.substring(lastIndex, match.index);
    }

    // Parse attributes from the entity tag
    const attributes = match[1];
    const typeMatch = attributes.match(/type\s*=\s*["']([^"']*?)["']/);
    const nameMatch = attributes.match(/name\s*=\s*["']([^"']*?)["']/);
    const idMatch = attributes.match(/id\s*=\s*["']([^"']*?)["']/);
    const subtitleMatch = attributes.match(/subtitle\s*=\s*["']([^"']*?)["']/);
    const typeNameMatch = attributes.match(/typeName\s*=\s*["']([^"']*?)["']/);

    const entityType = typeMatch ? typeMatch[1] : "";
    const entityName = nameMatch ? nameMatch[1] : "";
    const entityId = idMatch ? idMatch[1] : "";
    const entitySubtitle = subtitleMatch ? subtitleMatch[1] : "";
    const entityTypeName = typeNameMatch ? typeNameMatch[1] : entityType; // fallback to type if typeName not found

    // Create mention chip HTML with the entity data
    htmlContent += createMentionChipHtml(
      entityTypeName, // typeName (for display)
      entityName, // title
      entityType, // entityType
      entityId, // entityId
      entitySubtitle // subtitle
    );

    lastIndex = entityRegex.lastIndex;
  }

  // Add any remaining text after the last entity tag
  if (lastIndex < text.length) {
    htmlContent += text.substring(lastIndex);
  }

  return htmlContent;
};

/**
 * Common ContentEditable component for mentions with consistent styling
 */
export const MentionContentEditable = ({
  html = "",
  disabled = true,
  onChange = () => {},
  onFocus,
  onBlur,
  onKeyDown,
  onInput,
  style = {},
  className = "",
  placeholder,
  ...props
}) => {
  const defaultStyle = {
    whiteSpace: "pre-wrap",
    overflowWrap: "break-word",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    outline: "none",
    fontSize: "14px",
    lineHeight: 1.5,
    cursor: "text",
    userSelect: "text",
    WebkitUserSelect: "text",
    MozUserSelect: "text",
    msUserSelect: "text",
    ...style,
  };

  return (
    <ContentEditable
      html={html}
      disabled={disabled}
      onChange={onChange}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      onInput={onInput}
      tagName="div"
      style={defaultStyle}
      className={className}
      placeholder={placeholder}
      {...props}
    />
  );
};

export default {
  createMentionChipHtml,
  convertTextWithXmlTagsToHtml,
  MentionContentEditable,
};
